import { createBrowserRouter, Navigate, type RouteObject } from 'react-router'
import { appModules } from './moduleRegistry'
import { NotFoundState } from '@/components/feedback/States'
import { RequireAuth, RequireModule, RequirePermission } from '@/features/auth/guards'
import { AdminLayout, FamilyLayout, StaffLayout } from '@/layouts/PortalLayouts'
import { PlatformShell } from '@/layouts/platform/PlatformShell'
import { PlatformAuthProvider, RequireOperator } from '@/features/platform/PlatformAuth'
import { PlatformLoginPage } from '@/pages/platform/PlatformLoginPage'
import { PlatformSchoolPage } from '@/pages/platform/PlatformSchoolPage'
import { PlatformSchoolsPage } from '@/pages/platform/PlatformSchoolsPage'
import { PlatformSettingsPage } from '@/pages/platform/PlatformSettingsPage'
import { AccountPage } from '@/pages/account/AccountPage'
import { ClassDetailPage } from '@/pages/admin/academic/ClassDetailPage'
import { ClassesPage } from '@/pages/admin/academic/ClassesPage'
import { ShiftsPage } from '@/pages/admin/academic/ShiftsPage'
import { SubjectsPage } from '@/pages/admin/academic/SubjectsPage'
import { YearsPage } from '@/pages/admin/academic/YearsPage'
import { DashboardPage } from '@/pages/admin/DashboardPage'
import { SchoolSettingsPage } from '@/pages/admin/settings/SchoolSettingsPage'
import { UsersPage } from '@/pages/admin/settings/UsersPage'
import { HomepageSettingsPage } from '@/pages/admin/settings/HomepageSettingsPage'
import { StaffDetailPage } from '@/pages/admin/staff/StaffDetailPage'
import { StaffFormPage } from '@/pages/admin/staff/StaffFormPage'
import { StaffListPage } from '@/pages/admin/staff/StaffListPage'
import { StaffSetupPage } from '@/pages/admin/staff/StaffSetupPage'
import { StudentAdmitPage } from '@/pages/admin/students/StudentAdmitPage'
import { StudentDetailPage } from '@/pages/admin/students/StudentDetailPage'
import { StudentEditPage } from '@/pages/admin/students/StudentEditPage'
import { StudentsListPage } from '@/pages/admin/students/StudentsListPage'
import { AcceptInvitationPage } from '@/pages/auth/AcceptInvitationPage'
import { LoginPage } from '@/pages/auth/LoginPage'
import { AdmissionStatusPage } from '@/pages/admissions/AdmissionStatusPage'
import { PublicAdmissionPage } from '@/pages/admissions/PublicAdmissionPage'
import { HostGate } from '@/features/site/HostGate'
import { WebsitePreview } from '@/pages/site/WebsitePreview'
import { SchoolHomePage } from '@/pages/site/SchoolHomePage'
import { FamilyDashboardPage } from '@/pages/family/FamilyDashboardPage'
import { FamilySchoolPage } from '@/pages/family/FamilySchoolPage'
import { StaffClassesPage } from '@/pages/staff-portal/StaffClassesPage'
import { StaffHomePage } from '@/pages/staff-portal/StaffHomePage'
import { StaffProfilePage } from '@/pages/staff-portal/StaffProfilePage'

/** Module routes, behind a "not enabled" guard for optional school modules. */
function moduleRoutes(portal: 'admin' | 'staff' | 'family'): RouteObject[] {
  return appModules.flatMap((m) => {
    const routes = m.routes?.[portal] ?? []
    return m.schoolModule && routes.length
      ? [{ element: <RequireModule module={m.schoolModule} />, children: routes }]
      : routes
  })
}

export const router = createBrowserRouter([
  {
    // The address decides the site: website, operator console, one school, or all (single address).
    element: <HostGate />,
    children: [
      // The school's public homepage when it has one, else the sign-in page.
      { path: '/', element: <SchoolHomePage /> },
      { path: '/site', element: <WebsitePreview /> },
      { path: '/login', element: <LoginPage /> },
      { path: '/invite/:token', element: <AcceptInvitationPage /> },
      // Public online admission form of the school at this address, and the family's follow-up page.
      { path: '/inscription', element: <PublicAdmissionPage /> },
      { path: '/inscription/suivi', element: <AdmissionStatusPage /> },
      {
        // Operator console: its own session, separate from the schools' sign-in.
        path: '/platform',
        element: <PlatformAuthProvider />,
        children: [
          { path: 'login', element: <PlatformLoginPage /> },
          {
            element: <RequireOperator />,
            children: [
              {
                element: <PlatformShell />,
                children: [
                  { index: true, element: <PlatformSchoolsPage /> },
                  { path: 'schools/:id', element: <PlatformSchoolPage /> },
                  { path: 'settings', element: <PlatformSettingsPage /> },
                  { path: '*', element: <NotFoundState homeHref="/platform" /> },
                ],
              },
            ],
          },
        ],
      },
      {
        path: '/admin',
        element: <RequireAuth portal="admin" />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              {
                index: true,
                element: (
                  <RequirePermission permission="dashboard.view">
                    <DashboardPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'students',
                element: <RequirePermission permission="students.view" />,
                children: [
                  { index: true, element: <StudentsListPage /> },
                  {
                    path: 'new',
                    element: (
                      <RequirePermission permission="students.manage">
                        <StudentAdmitPage />
                      </RequirePermission>
                    ),
                  },
                  { path: ':id', element: <StudentDetailPage /> },
                  {
                    path: ':id/edit',
                    element: (
                      <RequirePermission permission="students.manage">
                        <StudentEditPage />
                      </RequirePermission>
                    ),
                  },
                ],
              },
              {
                path: 'staff',
                element: <RequirePermission permission="staff.view" />,
                children: [
                  { index: true, element: <StaffListPage /> },
                  {
                    path: 'new',
                    element: (
                      <RequirePermission permission="staff.manage">
                        <StaffFormPage />
                      </RequirePermission>
                    ),
                  },
                  { path: 'departments', element: <StaffSetupPage kind="departments" /> },
                  { path: 'designations', element: <StaffSetupPage kind="designations" /> },
                  { path: ':id', element: <StaffDetailPage /> },
                  {
                    path: ':id/edit',
                    element: (
                      <RequirePermission permission="staff.manage">
                        <StaffFormPage />
                      </RequirePermission>
                    ),
                  },
                ],
              },
              {
                path: 'academic',
                element: <RequirePermission permission="academic.view" />,
                children: [
                  { index: true, element: <Navigate to="classes" replace /> },
                  { path: 'years', element: <YearsPage /> },
                  { path: 'classes', element: <ClassesPage /> },
                  { path: 'classes/:id', element: <ClassDetailPage /> },
                  { path: 'subjects', element: <SubjectsPage /> },
                  { path: 'shifts', element: <ShiftsPage /> },
                ],
              },
              {
                path: 'settings',
                children: [
                  { index: true, element: <Navigate to="school" replace /> },
                  {
                    path: 'school',
                    element: (
                      <RequirePermission permission="school.manage">
                        <SchoolSettingsPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'homepage',
                    element: (
                      <RequirePermission permission="school.manage">
                        <HomepageSettingsPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'users',
                    element: (
                      <RequirePermission permission="users.manage">
                        <UsersPage />
                      </RequirePermission>
                    ),
                  },
                ],
              },
              { path: 'account', element: <AccountPage /> },
              ...moduleRoutes('admin'),
              { path: '*', element: <NotFoundState homeHref="/admin" /> },
            ],
          },
        ],
      },
      {
        path: '/staff',
        element: <RequireAuth portal="staff" />,
        children: [
          {
            element: <StaffLayout />,
            children: [
              { index: true, element: <StaffHomePage /> },
              { path: 'classes', element: <StaffClassesPage /> },
              { path: 'profile', element: <StaffProfilePage /> },
              { path: 'account', element: <AccountPage /> },
              ...moduleRoutes('staff'),
              { path: '*', element: <NotFoundState homeHref="/staff" /> },
            ],
          },
        ],
      },
      {
        path: '/portal',
        element: <RequireAuth portal="family" />,
        children: [
          {
            element: <FamilyLayout />,
            children: [
              { index: true, element: <FamilyDashboardPage /> },
              { path: 'school', element: <FamilySchoolPage /> },
              { path: 'account', element: <AccountPage /> },
              ...moduleRoutes('family'),
              { path: '*', element: <NotFoundState homeHref="/portal" /> },
            ],
          },
        ],
      },
      {
        path: '*',
        element: (
          <div className="min-h-dvh bg-canvas pt-20">
            <NotFoundState homeHref="/login" />
          </div>
        ),
      },
    ],
  },
])
